import { capitalize } from 'vue';

import moment from 'moment';

import i18n from '@/config/i18n';

import getDateRange from '@/utils/getDateRange';
import formatValue from '@/utils/formatValue';
import formatDate from '@/utils/formatDate';

import useTooltipState from '@/state/useTooltipState';
import useFilterState from '@/state/useFilterState';
import useSectionState from '@/state/useSectionState';

const {
  setPos,
  setTooltipData,
  tooltipRef,
} = useTooltipState();

const {
  dateBegin,
  dateEnd,
  ctpDateBegin,
  ctpDateEnd,
  ctpTag,
} = useFilterState();
const { getChartById } = useSectionState();

const calculatePosition = (event, tooltipBox, offsetX, offsetY) => {
  // default tooltip X (pointer's right side)
  let newX = event.clientX + offsetX;

  // flip tooltip horizontally to the left side if the box exceeds window right side limit
  if (newX + tooltipBox.width > window.innerWidth) {
    newX = event.clientX - offsetX - tooltipBox.width;
  }

  // default tooltip Y (above pointer)
  let newY = event.clientY - offsetY - tooltipBox.height;

  // flip tooltip vertically below the pointer if the box exceeds window top limit
  if (newY - tooltipBox.height < 0) {
    newY = event.clientY + offsetY;
  }

  // set new coordinates
  setPos(newX, newY);
};

const tooltipHandler = (handler, event, item, values) => {
  tooltipRef.value.style.visibility = 'hidden';

  // hide tooltip for null, undefined, or empty string values
  if (typeof values !== 'object') {
    return;
  }

  // eslint-disable-next-line no-underscore-dangle
  const { id } = handler._el;

  const {
    type, dataFormat, selectedInterval, specConfig,
  } = getChartById(id);

  let hoverData;
  const isLineChart = !!type.match(/line/);
  const isScoreChart = !!id.match(/score/);
  const isBarChart = !!type.match(/bar/);
  const isBoxplotChart = !!type.match(/boxplot/);
  const isCurvesChart = !!id.match(/curve/);
  const isKpiChart = !!id.match(/kpi/);
  const isSingleDivergingChart = !!id.match(/single_diverging/);

  if (isLineChart && values.matches.length > 1) {
    hoverData = values.matches;
  } else {
    hoverData = [values];
  }

  const data = hoverData.map((i) => {
    let date;
    let pastDate;
    if (selectedInterval) {
      date = formatDate(i.date, selectedInterval);
      pastDate = formatDate(i.pastDate, selectedInterval);
    } else {
      date = moment(i.date).format('MMM, YYYY');
      pastDate = moment(i.pastDate || i.date).format('MMM, YYYY');
    }

    const isCurrentMonth = moment(i.date).isSame(new Date(), 'month');

    // still no date? use selected dates
    if (!i.date) {
      date = getDateRange(dateBegin.value, dateEnd.value);
    }

    if (!i.pastDate) {
      pastDate = getDateRange(ctpDateBegin.value, ctpDateEnd.value);
    }

    const isCtp = isKpiChart;

    const formattedItem = {
      compset: i.compset,
      metric: i.metric,
      metricTag: i.metricTag,
      id,
      isOwn: i.isOwn,
      value: formatValue(i.currentValue || i.value || i.median, dataFormat, isSingleDivergingChart),
      pastValue: formatValue(i.pastValue || i.pastMedian, dataFormat, isSingleDivergingChart),
      lastScore: i.lastScore,
      diff: i.diff,
      formattedDiff: formatValue(i.diff, '.1~%'),
      date,
      pastDate,
      isCurrentMonth,
      color: i.color,
      patternId: i.patternId,
      chartType: type,
      // curves charts only
      days: i.step,
      // types
      isLineChart,
      isScoreChart,
      isBarChart,
      isBoxplotChart,
      isCurvesChart,
      isCtp: isCtp || ctpTag.value,
    };

    if (id.match(/correlated/)) {
      const { xConfig, yConfig } = specConfig;

      formattedItem.correlatedConfig = {
        over: {
          name: xConfig.title,
          value: formatValue(i.over, xConfig.dataFormat),
        },
        metric: {
          name: yConfig.title,
          value: formatValue(i.value, yConfig.dataFormat),
        },
      };
    }

    if (type.match(/stack/) && i.source) {
      formattedItem.source = i18n.global.t(capitalize(i.source));
    }

    return formattedItem;
  });

  // send current hover data
  setTooltipData(data);

  // make tooltip visible
  tooltipRef.value.style.visibility = 'visible';

  calculatePosition(
    event, // mouse event
    tooltipRef.value.getBoundingClientRect(), // tooltip element
    10, // box offset X
    10, // box offset y
  );
};

export default tooltipHandler;
